import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Snackbar,
  Switch,
  Typography,
} from '@mui/material';
import {
  useGetMyNotificationPreferencesQuery,
  useUpdateMyNotificationPreferencesMutation,
} from '../../app/notificationPreferencesApi';
import { ADMIN_SITEWIDE_ACCESS } from '../projects/aclActions';
import { PageTitle } from '../../components/StyledComponents';

const NOTIFICATION_CATEGORIES = [
  {
    category: 'Workflow Notifications',
    requiredAction: 'view-approval-queue',
    types: [
      {
        notificationType: 'internal_review',
        label: 'Deliverable Submitted',
        description:
          'Notify me when a deliverable or form is ready for internal review.',
      },
      {
        notificationType: 'federal_review',
        label: 'Deliverable Submitted',
        description:
          'Notify me when a deliverable or form is ready for federal review.',
      },
      {
        notificationType: 'approval_complete',
        label: 'Deliverable Approved',
        description: 'Notify me when a new deliverable is approved for my program.',
      },
    ],
  },
  {
    category: 'Project & Program Notifications',
    requiredAction: 'list-projects',
    types: [
{
        notificationType: 'new_project',
        label: 'New Project',
        description: 'Notify me when a new project is added to my program.',
      },
    ],
  },
  {
    category: 'Financial Notifications',
    requiredAction: 'view-financial-views',
    types: [
      {
        notificationType: 'financial_plan_created',
        label: 'Financial Plan Created',
        description: 'Notify me when a new financial plan is created for my program.',
      },
      {
        notificationType: 'financial_plan_updated',
        label: 'Financial Plan Updated',
        description: 'Notify me when a financial plan in my program is updated.',
      },
    ],
  },
  {
    category: 'Schedule Notifications',
    requiredAction: 'view-ims-summary',
    types: [
      {
        notificationType: 'ims_upload',
        label: 'Master Schedule Uploaded',
        description: 'Notify me when a new Master Schedule is uploaded to my project.',
      },
    ],
  },
];


const Notifications = () => {
  const userActions = useSelector((state) => state.user.actions);
  const isAdmin = userActions?.some(
    (a) => a.action === ADMIN_SITEWIDE_ACCESS,
  );

  const visibleCategories = useMemo(
    () =>
      NOTIFICATION_CATEGORIES.filter(
        ({ requiredAction }) =>
          isAdmin || userActions?.some((a) => a.action === requiredAction),
      ),
    [userActions, isAdmin],
  );

  const visibleTypes = useMemo(
    () => visibleCategories.flatMap((cat) => cat.types),
    [visibleCategories],
  );

  const {
    data,
    isLoading,
    isError,
  } = useGetMyNotificationPreferencesQuery();

  const [updatePreferences, { isLoading: isSaving }] =
    useUpdateMyNotificationPreferencesMutation();

  const buildVisibleToggles = (defaults) => {
    const result = {};
    visibleTypes.forEach(({ notificationType }) => {
      result[notificationType] = defaults?.[notificationType] ?? true;
    });
    return result;
  };

  const [toggles, setToggles] = useState(() => buildVisibleToggles());
  const [hasChanges, setHasChanges] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (data?.preferences && data.preferences.length > 0) {
      const fromApi = {};
      data.preferences.forEach(({ notificationType, enabled }) => {
        fromApi[notificationType] = enabled;
      });
      // Merge: any type not returned by the API defaults to true (opted-in)
      setToggles(buildVisibleToggles(fromApi));
      setHasChanges(false);
    }
  }, [data, visibleTypes]);

  const handleToggle = (notificationType) => {
    setToggles((prev) => ({
      ...prev,
      [notificationType]: !prev[notificationType],
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    const preferences = visibleTypes.map(({ notificationType }) => ({
      notificationType,
      enabled: toggles[notificationType],
    }));

    try {
      await updatePreferences({ preferences }).unwrap();
      setHasChanges(false);
      setSnackbar({
        open: true,
        message: 'Preferences saved.',
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to save notification preferences. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { sm: '100%', md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
      }}
    >
      <Box sx={{ p: 3, flex: 1 }}>
        {/* Page Header */}
        <PageTitle sx={{ mb: 1 }}>Email Notifications</PageTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose which email notifications you'd like to receive.
        </Typography>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {isError && !isLoading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load notification preferences. Please refresh the page.
          </Alert>
        )}

        {/* Main Content — Category Cards */}
        {!isLoading && !isError && (
          <>
            {visibleCategories.length === 0 && (
              <Alert severity="info" sx={{ maxWidth: 640, mb: 3 }}>
                No notification preferences are available for your current role.
              </Alert>
            )}

            {visibleCategories.map(({ category, types }) => (
              <Paper
                key={category}
                variant="outlined"
                sx={{ p: 3, maxWidth: 640, mb: 3 }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {category}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <FormGroup>
                  {types.map(({ notificationType, label, description }) => (
                    <Box
                      key={notificationType}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {description}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={toggles[notificationType] ?? true}
                            onChange={() => handleToggle(notificationType)}
                            inputProps={{
                              'aria-label': `Toggle ${label} notifications`,
                            }}
                          />
                        }
                        label=""
                        sx={{ ml: 2, mr: 0 }}
                      />
                    </Box>
                  ))}
                </FormGroup>
              </Paper>
            ))}

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', maxWidth: 640 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                sx={{
                  background: 'rgb(31, 77, 131)',
                  '&:hover': {
                    background: 'rgb(0, 136, 255)',
                  },
                  color: (theme) => theme.palette.secondary.contrastText,
                  textTransform: 'none',
                }}
              >
                {isSaving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* Snackbar Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default Notifications;
